import { JSX, useContext, useEffect, useMemo } from "react";
import { NetworkIntegrityJobStatus, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityJobAction } from "@app/projects/network-assessment/network-integrity/state-management/networkIntegrityJobStateManagement";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

const NetworkIntegrityJobStatusBanner = (): JSX.Element => {
  const message = "The data calculated for this dashboard is being processed and will be available soon.";
  const adequacyImpactMessage = "The data calculated for Adequacy Impact is being processed and will be available soon.";
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);

  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const project = networkIntegrityContext.project!;
  const projectId = project.projectId;

  // Every Network Integrity widget gates its own fetch on the job status this effect populates.
  useEffect(() => {
    async function fetchJobStatus() {
      networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.IsJobLoadingChanged, value: { isJobLoading: true } });
      try {
        if (networkIntegrityContext.projectRunConfigurationId) {
          const isSecondary = networkIntegrityContext.projectRunConfigurationDualType === ProjectRunConfigurationType.DualMembershipSupplemental;
          const response = await networkIntegrityService.getJobStatus(projectId, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, isSecondary);
          networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.JobStatusChanged, value: { canResubmit: response.canResubmit, isAdequacyImpactExpected: response.isAdequacyImpactExpected, isAdequacyImpactProcessing: response.isAdequacyImpactProcessing, isOpportunityServiceArea: response.isOpportunityServiceArea, jobStatus: response.jobStatus } });
        }
      } finally {
        networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.IsJobLoadingChanged, value: { isJobLoading: false } });
      }
    }
    fetchJobStatus();
  }, [
    networkIntegrityContext.projectRunConfigurationId,
    networkIntegrityContext.providerType,
    networkIntegrityContext.projectRunConfigurationDualType,
    networkIntegrityContext.jobStatusChanged,
    projectId,
    networkIntegrityService
  ])

  const handleRunAgain = async () => {
    const previousJobState = {
      canResubmit: networkIntegrityContext.canResubmit,
      isAdequacyImpactExpected: networkIntegrityContext.isAdequacyImpactExpected,
      isAdequacyImpactProcessing: networkIntegrityContext.isAdequacyImpactProcessing,
      isOpportunityServiceArea: networkIntegrityContext.isOpportunityServiceArea,
      jobStatus: networkIntegrityContext.jobStatus,
    };
    networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.JobStatusChanged, value: { canResubmit: false, isAdequacyImpactExpected: networkIntegrityContext.isAdequacyImpactExpected, isAdequacyImpactProcessing: false, isOpportunityServiceArea: networkIntegrityContext.isOpportunityServiceArea, jobStatus: NetworkIntegrityJobStatus.Queued } });
    try {
      await networkIntegrityService.resubmitFailedJobs(projectId, networkIntegrityContext.projectRunConfigurationId);
    } catch (error) {
      networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.JobStatusChanged, value: previousJobState });
      throw error;
    }
  };

  // The job status is resolved per run configuration, so it describes the selected run whether or not that run
  // is still the current one. A superseded run whose jobs failed or are still running is reported the same way,
  // and a run that finished cleanly resolves to Finished and renders nothing. Resubmit availability is decided
  // server side by canResubmit, not here.
  if(networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Failed)
    return !networkIntegrityContext.isJobStatusLoading &&
      <QESAlert type="danger" key="readonly" persist={true}>
        <strong>Warning!</strong> Network Integrity job has Failed.
        {networkIntegrityContext.canResubmit && <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN]}>
          <QESButton className="ms-3" qesType="secondary" onClick={handleRunAgain} qesTestId="network-integrity_failed-job-run-again">
            <QesIcon iconName={FontAwesome.Rocket} width="14" height="16" style={{ paddingBottom: "2px" }} isDecorative /> Run Again
          </QESButton>
        </RoleRestricted>}
      </QESAlert>;

  if (!networkIntegrityContext.isJobStatusLoading
      && networkIntegrityContext.jobStatus == NetworkIntegrityJobStatus.Finished
      && networkIntegrityContext.isAdequacyImpactProcessing)
    return <QESAlert type="info" key="adequacy-impact-processing" persist={true}>{adequacyImpactMessage}</QESAlert>;

  return !networkIntegrityContext.isJobStatusLoading
          && networkIntegrityContext.jobStatus
          && networkIntegrityContext.jobStatus != NetworkIntegrityJobStatus.Finished
          && <QESAlert type="info" key="readonly" persist={true}>{message}</QESAlert>;
}

export { NetworkIntegrityJobStatusBanner };